import { fetchAPI, ErrorResponse } from '@quickbit/backoffice-core-frontend';
import { MoneyScaled } from '@quickbit/qb-design-system';
import { useQuery } from '@tanstack/react-query';

type PriceLevel = 'buy' | 'sell' | 'mid';

type Rate = Record<PriceLevel, number>;
type Rates = Record<'usdt', Rate>;

export const fetchExchangeRate = async (): Promise<number> => {
  const { rates } = await fetchAPI<{
    rates: Rates;
    referenceTimestamp: number;
  }>('v1/exchange-rates');
  return rates.usdt.mid;
};

interface SendExternalUsdcData {
  amount: MoneyScaled;
  address: string;
  isMax: boolean;
  label?: string;
}

interface SendPreviewResponse {
  previewId: string;
  isInternal: boolean;
  hasTravelRuleCollectedData: boolean;
}

export const createSendPreview = async (
  currency: string,
  { amount, address, isMax, label }: SendExternalUsdcData
) => {
  return await fetchAPI<SendPreviewResponse>(
    `v1/merchants/${currency}/send/preview`,
    {
      method: 'POST',
      body: {
        amount,
        destinationAddress: address,
        label,
        ...(isMax && { feeModel: 'SUBTRACTION' }),
      },
    }
  );
};

export const completeSendOrder = (previewId: string): Promise<void> => {
  return fetchAPI(`v1/merchants/send/${previewId}/complete`, {
    method: 'POST',
  });
};

export interface TravelRuleInfoStatus {
  status: 'NEEDS_TRAVEL_RULE_INFO' | 'NEEDS_POO' | 'READY';
}

export const getTravelRuleInfoStatus = async (sendPreviewId: string) => {
  return fetchAPI<TravelRuleInfoStatus>(
    `v1/merchants/travel-rule-info/${sendPreviewId}/status`
  );
};

const queryKeys = {
  all: () => ['travel-rule'] as const,
  status: (sendPreviewId: string) =>
    [...queryKeys.all(), 'status', sendPreviewId] as const,
};

export const useTravelRuleStatusPolling = ({
  sendPreviewId,
  enabled = false,
}: {
  sendPreviewId: string | undefined;
  enabled?: boolean;
}) => {
  return useQuery<TravelRuleInfoStatus, ErrorResponse>({
    queryKey: queryKeys.status(sendPreviewId ?? ''),
    queryFn: () => getTravelRuleInfoStatus(sendPreviewId as string),
    enabled: enabled && !!sendPreviewId,
    refetchInterval: (query) => {
      // Stop polling when status is READY
      if (query.state.data?.status === 'READY') {
        return false;
      }
      return 3000;
    },
    refetchIntervalInBackground: true,
  });
};
